import api from './api';

export interface FieldMapping {
  id: number;
  intake_form_type_id: number;
  form_field: string;
  label?: string;
  template_variable: string;
  field_type: string;
  section: string;
  is_required: boolean;
  is_hidden: boolean;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateFieldMappingData {
  form_field: string;
  label?: string;
  template_variable: string;
  field_type: string;
  section?: string;
  is_required?: boolean;
  is_hidden?: boolean;
  description?: string;
  sort_order?: number;
}

export interface UpdateFieldMappingData extends CreateFieldMappingData {
  id: number;
}

export interface FieldTypes {
  [key: string]: string;
}

export interface Sections {
  [key: string]: string;
}

// API response structure
export interface FieldMappingResponse {
  data: FieldMapping[];
  field_types: FieldTypes;
  sections: Sections;
}

// Get field mappings for a specific intake form type
export const getByIntakeFormType = (intakeFormTypeId: number) =>
  api.get<FieldMappingResponse>(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings`
  );

// Get field mappings by service type (for dynamic form loading)
export const getByServiceType = (serviceTypeId: number) =>
  api.get<FieldMappingResponse>(
    `/admin/service-types/${serviceTypeId}/field-mappings`
  );

// Get field mappings by service type and intake form type
export const getByServiceTypeAndIntakeFormType = (
  serviceTypeId: number,
  intakeFormTypeId: number
) =>
  api.get<FieldMappingResponse>(
    `/admin/service-types/${serviceTypeId}/intake-form-types/${intakeFormTypeId}/field-mappings`
  );

// Create a new field mapping
export const create = (
  intakeFormTypeId: number,
  data: CreateFieldMappingData
) =>
  api.post<FieldMapping>(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings`,
    data
  );

// Update an existing field mapping
export const update = (
  intakeFormTypeId: number,
  mappingId: number,
  data: UpdateFieldMappingData
) =>
  api.put<FieldMapping>(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings/${mappingId}`,
    data
  );

// Delete a field mapping
export const deleteMapping = (intakeFormTypeId: number, mappingId: number) =>
  api.delete(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings/${mappingId}`
  );

// Get field mappings by section
export const getBySection = (intakeFormTypeId: number, section: string) =>
  api.get<FieldMappingResponse>(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings/section/${section}`
  );

// Get required field mappings
export const getRequired = (intakeFormTypeId: number) =>
  api.get<FieldMappingResponse>(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings/required`
  );

// Bulk update field mappings
export const bulkUpdate = (
  intakeFormTypeId: number,
  mappings: UpdateFieldMappingData[]
) =>
  api.post(
    `/admin/intake-form-types/${intakeFormTypeId}/field-mappings/bulk-update`,
    { mappings }
  );

// Get available field types
export const getFieldTypes = (): FieldTypes => ({
  text: 'Text',
  number: 'Number',
  date: 'Date',
  boolean: 'Boolean (Yes/No)',
  array: 'Array/List',
  email: 'Email',
  phone: 'Phone Number',
  address: 'Address',
  signature: 'Signature',
});

// Get available sections
export const getSections = (): Sections => ({
  personal_info: 'Personal Information',
  legal_issues: 'Legal Issues',
  education_training: 'Education & Training',
  work_history: 'Work History',
  benefits_information: 'Benefits Information',
  medical_history: 'Medical History',
  therapy_information: 'Therapy Information',
  activities_daily_living: 'Activities of Daily Living',
  signatures: 'Signatures',
});

// Section visibility interfaces
export interface SectionSetting {
  section: string;
  section_name: string;
  is_hidden: boolean;
  sort_order: number;
}

export interface SectionSettingsResponse {
  data: SectionSetting[];
  sections: Sections;
}

export interface UpdateSectionSettingsData {
  sections: Array<{
    section: string;
    is_hidden: boolean;
    sort_order?: number;
  }>;
}

// Get section settings for an intake form type
export const getSectionSettings = (intakeFormTypeId: number) =>
  api.get<SectionSettingsResponse>(
    `/admin/intake-form-types/${intakeFormTypeId}/sections`
  );

// Update section visibility settings
export const updateSectionSettings = (
  intakeFormTypeId: number,
  data: UpdateSectionSettingsData
) =>
  api.put<{ message: string; data: SectionSetting[] }>(
    `/admin/intake-form-types/${intakeFormTypeId}/sections`,
    data
  );

// Get visible sections for an intake form type
export const getVisibleSections = (intakeFormTypeId: number) =>
  api.get<{ data: string[] }>(
    `/admin/intake-form-types/${intakeFormTypeId}/sections/visible`
  );

// Default export for backward compatibility
const fieldMappingService = {
  getByIntakeFormType,
  getByServiceType,
  getByServiceTypeAndIntakeFormType,
  create,
  update,
  deleteMapping,
  getBySection,
  getRequired,
  bulkUpdate,
  getFieldTypes,
  getSections,
  getSectionSettings,
  updateSectionSettings,
  getVisibleSections,
};

export default fieldMappingService;
